// src/services/authService.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { fetchCustomToken } from "../utils/azureUtils";

const auth = getAuth();

const postFormData = async (url, data) => {
  const response = await fetch(url, {
    method: "POST",
    body: data,
  });
  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }
  return await response.text();
};

const createTvSession = async (user, pairingCode, authResponseData) => {
  const authResponseJson = JSON.parse(authResponseData);
  const kbeanToken = authResponseJson.token;
  const customToken = await fetchCustomToken(user.uid);
  try {
    const expirationTime = Timestamp.fromDate(
      new Date(Date.now() + 1 * 60 * 1000)
    ); // 1 minute from now
    await setDoc(doc(db, "sessions", pairingCode), {
      userId: user.uid,
      email: user.email,
      token: customToken,
      kbeanToken: kbeanToken,
      authenticated: true,
      expiration: expirationTime,
    });
    console.log("TV session document created successfully.");
  } catch (error) {
    console.error("Failed to create TV session document:", error);
    throw error;
  }
};

const handleThirdPartyAPICalls = async (user, pairingCode, isConnectRoute) => {
  const token = await user.getIdToken();

  const socialLoginJWTURL = process.env.REACT_APP_SOCIAL_LOGIN_JWT_URL;
  const formData = new FormData();
  formData.append("fbtoken", token);
  formData.append("currentUser", JSON.stringify(user));
  await postFormData(socialLoginJWTURL, formData);

  const socialAuthenticateURL = process.env.REACT_APP_SOCIAL_AUTHENTICATE_URL;
  const authData = new FormData();
  authData.append("fbtoken", token);
  const authResponseData = await postFormData(socialAuthenticateURL, authData);
  localStorage.setItem("authResponseData", authResponseData);

  if (isConnectRoute && pairingCode) {
    await createTvSession(user, pairingCode, authResponseData);
  }
};

export const signUpUser = async (email, password) => {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  );
  const user = userCredential.user;
  await handleThirdPartyAPICalls(user, null, false);
  return user;
};

export const signInUserWithEmailPassword = async (
  email,
  password,
  pairingCode = null,
  isConnectRoute = false
) => {
  const userCredential = await signInWithEmailAndPassword(
    auth,
    email,
    password
  );
  const user = userCredential.user;
  await handleThirdPartyAPICalls(user, pairingCode, isConnectRoute);
  return user;
};

export const signInWithGoogle = async (
  pairingCode = null,
  isConnectRoute = false
) => {
  const googleProvider = new GoogleAuthProvider();
  const result = await signInWithPopup(auth, googleProvider);
  const user = result.user;
  await handleThirdPartyAPICalls(user, pairingCode, isConnectRoute);
  return user;
};

export const signInWithApple = async (
  pairingCode = null,
  isConnectRoute = false
) => {
  const appleProvider = new OAuthProvider("apple.com");
  const result = await signInWithPopup(auth, appleProvider);
  const user = result.user;
  await handleThirdPartyAPICalls(user, pairingCode, isConnectRoute);
  return user;
};

export const getFriendlyErrorMessage = (errorCode) => {
  switch (errorCode) {
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/invalid-email":
      return "Please enter a valid email address.";
    case "auth/operation-not-allowed":
      return "Email/password accounts are not enabled.";
    case "auth/weak-password":
      return "Your password is too weak. Please use a stronger password.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/user-not-found":
      return "No account found with this email address.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    default:
      return "An unexpected error occurred. Please try again later.";
  }
};

// New function: sendPasswordResetEmail
export const sendPasswordResetEmail = async (email) => {
  return firebaseSendPasswordResetEmail(auth, email);
};
